import React, { useEffect, useState } from 'react';
import Add from '@mui/icons-material/Add';
import Remove from '@mui/icons-material/Remove';
import { Box, Button, Paper, Typography } from '@mui/material';

import noImage from '../../assets/no-image.png';
import { v2Colors } from '../../design/theme';
import { useV2Common } from '../../state/CommonProvider';
import { addToCartItem, getCartLineKey, setCartLineQuantity } from '../../services/commerce';
import { getCartSuggestions } from '../../services/cart';

function getImageSrc(product) {
  if (product?.imgUrls?.length > 0) return product.imgUrls[0];
  if (product?.imgUrl) return product.imgUrl;
  if (product?.image) return product.image;
  if (product?.images?.length > 0) return product.images[0];
  return null;
}

function lowestVisibleVariant(product) {
  return (product?.variants ?? [])
    .filter((v) => v.visibleToCustomer !== false && v.isActive !== false)
    .sort((a, b) => (Number(a.price) || 0) - (Number(b.price) || 0))[0];
}

function resolveProductId(product) {
  return product?.id || product?.documentId || null;
}

function suggestionPrice(product, variant) {
  if (variant && Number(variant.price) > 0) return Number(variant.price);
  return Number(product?.price) || 0;
}

function suggestionVariant(product) {
  const productId = resolveProductId(product);
  const v = lowestVisibleVariant(product);
  if (v) return v;
  return {
    sku: productId,
    price: suggestionPrice(product, null),
    visibleToCustomer: true,
    isActive: true,
  };
}

function paidLinesForProduct(cart, productId) {
  if (!productId || !cart?.products?.length) return [];
  const pid = String(productId);
  return cart.products.filter((p) => String(p.productId) === pid && !p.isFreeSkuItem);
}

function cartQtyForProduct(cart, productId) {
  return paidLinesForProduct(cart, productId).reduce(
    (sum, line) => sum + (Number(line.quantity) || 0),
    0
  );
}

/** Pack label for subtitle — avoid numeric `quantity` (cart count / stock). */
function packLabel(product) {
  if (product?.qty && typeof product.qty === 'string') return product.qty;
  const variant = lowestVisibleVariant(product);
  if (variant?.weight) return String(variant.weight);
  const q = product?.quantity;
  if (typeof q === 'string' && q.trim() && Number.isNaN(Number(q))) return q.trim();
  const name = String(product?.name || '');
  const packMatch = name.match(/\(\s*(\d+\s*(?:Pc|Pack|pcs|pack)?[^)]*)\)/i);
  if (packMatch) return packMatch[1].trim();
  return null;
}

const actionBtnSx = {
  bgcolor: v2Colors.primary,
  color: v2Colors.white,
  fontSize: '0.55rem',
  fontWeight: 700,
  height: 26,
  borderRadius: 1,
  boxShadow: 'none',
  flexShrink: 0,
  '&:hover': { bgcolor: v2Colors.primaryDark, boxShadow: 'none' },
};

function SuggestionQtyStepper({ qty, disabled, onDecrease, onIncrease }) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        bgcolor: v2Colors.primary,
        borderRadius: 1,
        height: 26,
        flexShrink: 0,
        overflow: 'hidden',
      }}
    >
      <Box
        component="button"
        type="button"
        aria-label="Decrease quantity"
        disabled={disabled || qty <= 0}
        onClick={onDecrease}
        sx={{
          border: 'none',
          bgcolor: 'transparent',
          color: v2Colors.white,
          width: 26,
          height: 26,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: disabled || qty <= 0 ? 'not-allowed' : 'pointer',
          opacity: disabled || qty <= 0 ? 0.45 : 1,
          fontFamily: 'inherit',
          p: 0,
        }}
      >
        <Remove sx={{ fontSize: 14 }} />
      </Box>
      <Typography
        fontWeight={700}
        sx={{
          minWidth: 18,
          px: 0.25,
          textAlign: 'center',
          color: v2Colors.white,
          fontSize: '0.7rem',
          lineHeight: 1,
        }}
      >
        {qty}
      </Typography>
      <Box
        component="button"
        type="button"
        aria-label="Increase quantity"
        disabled={disabled}
        onClick={onIncrease}
        sx={{
          border: 'none',
          bgcolor: 'transparent',
          color: v2Colors.white,
          width: 26,
          height: 26,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.45 : 1,
          fontFamily: 'inherit',
          p: 0,
        }}
      >
        <Add sx={{ fontSize: 14 }} />
      </Box>
    </Box>
  );
}

export default function CartSuggestions({ cart }) {
  const { isDesktop } = useV2Common();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const hasCartItems = (cart?.products?.length ?? 0) > 0;

  useEffect(() => {
    if (!hasCartItems) {
      setSuggestions([]);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await getCartSuggestions(cart, { includeInCart: true });
        if (!cancelled) setSuggestions(data);
      } catch {
        if (!cancelled) setSuggestions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Only refetch when cart goes empty / non-empty — qty changes update stepper via `cart` prop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCartItems]);

  const handleQtyChange = async (product, delta) => {
    const productId = resolveProductId(product);
    if (!productId || busyId) return;

    const lines = paidLinesForProduct(cart, productId);
    const currentQty = lines.reduce((sum, l) => sum + (Number(l.quantity) || 0), 0);
    const nextQty = currentQty + delta;
    if (nextQty < 0) return;

    setBusyId(productId);
    try {
      if (currentQty === 0 && delta > 0) {
        const variant = suggestionVariant(product);
        await addToCartItem({
          productId,
          name: product.name || 'Product',
          imageUrl: getImageSrc(product) || noImage,
          variant,
          customizations: {},
          qty: 1,
        });
      } else if (lines.length) {
        // Adjust the most recently added line for this product (suggestions are simple, usually one line).
        const lastLine = lines[lines.length - 1];
        await setCartLineQuantity(
          { lineKey: getCartLineKey(lastLine) },
          (Number(lastLine.quantity) || 0) + delta
        );
      }
    } catch (err) {
      console.error('Failed to update suggestion qty:', err);
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          py: 2.5,
          textAlign: 'center',
          bgcolor: v2Colors.surface,
          borderRadius: 2,
          my: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Loading suggestions…
        </Typography>
      </Box>
    );
  }

  if (!suggestions.length) {
    return null;
  }

  return (
    <Box sx={{ my: 2 }}>
      <Typography
        sx={{
          fontSize: '1rem',
          fontWeight: 700,
          mb: 1.25,
          color: v2Colors.heading,
          textAlign: 'center',
        }}
      >
        One tap = better breakfast tomorrow!
      </Typography>

      <Box
        sx={{
          bgcolor: v2Colors.primary,
          p: 1.5,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            overflowX: 'auto',
            gap: 1.5,
            pb: 1,
            '&::-webkit-scrollbar': { height: 6 },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(255,255,255,0.2)',
              borderRadius: 10,
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(255,255,255,0.6)',
              borderRadius: 10,
            },
          }}
        >
          {suggestions.map((product) => {
            const productId = resolveProductId(product);
            const imageSrc = getImageSrc(product);
            const cardW = isDesktop ? 260 : 200;
            const imgSize = isDesktop ? 64 : 56;
            const variant = suggestionVariant(product);
            const displayPrice = suggestionPrice(product, variant);
            const inCartQty = cartQtyForProduct(cart, productId);
            const isBusy = busyId === productId;
            const subtitle = packLabel(product);

            return (
              <Box key={productId || product.name} sx={{ width: cardW, flexShrink: 0 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.25,
                    bgcolor: v2Colors.surface,
                    borderRadius: 2.5,
                    display: 'flex',
                    alignItems: 'stretch',
                    gap: 1,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      width: imgSize,
                      height: imgSize,
                      borderRadius: 1.5,
                      overflow: 'hidden',
                      bgcolor: v2Colors.white,
                      flexShrink: 0,
                      alignSelf: 'center',
                    }}
                  >
                    {imageSrc ? (
                      <Box
                        component="img"
                        src={imageSrc}
                        alt={product.name}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = noImage;
                        }}
                      />
                    ) : (
                      <Typography
                        sx={{
                          color: v2Colors.textMuted,
                          fontSize: '0.5rem',
                          fontWeight: 700,
                          textAlign: 'center',
                          lineHeight: `${imgSize}px`,
                        }}
                      >
                        IMG
                      </Typography>
                    )}
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: isDesktop ? '0.75rem' : '0.68rem',
                        fontWeight: 700,
                        lineHeight: 1.15,
                        color: v2Colors.heading,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {product.name}
                    </Typography>

          

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 0.75,
                        mt: 'auto',
                        pt: 0.25,
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: isDesktop ? '0.8rem' : '0.72rem',
                          fontWeight: 700,
                          color: v2Colors.heading,
                          flexShrink: 0,
                        }}
                      >
                        ₹{displayPrice}
                      </Typography>
                      {inCartQty > 0 ? (
                        <SuggestionQtyStepper
                          qty={inCartQty}
                          disabled={isBusy}
                          onDecrease={() => handleQtyChange(product, -1)}
                          onIncrease={() => handleQtyChange(product, 1)}
                        />
                      ) : (
                        <Button
                          variant="contained"
                          size="small"
                          disabled={!productId || isBusy}
                          onClick={() => handleQtyChange(product, 1)}
                          sx={{
                            ...actionBtnSx,
                            minWidth: 52,
                            px: 1.5,
                            py: 0,
                          }}
                        >
                          Add
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Paper>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
